<template>
    <button
        class="base-button"
        :disabled="disabled || isLoading"
        :class="{
            'base-button--block': block,
            'base-button--danger': variant === 'danger',
            'base-button--ghost': variant === 'ghost',
            'base-button--light': variant === 'light',
            'base-button--medium': size === 'medium',
            'base-button--outline': variant === 'outline',
            'base-button--small': size === 'small',
            'base-button--with-icon':
                $slots.icon ||
                isDoneIconVisible ||
                isFailIconVisible ||
                isLoading,
        }"
        v-on="$listeners"
    >
        <VSpinner
            v-if="isLoading"
            class="base-button__icon"
            size="18"
            :line-bg-color="colorIcon"
            :line-fg-color="colorSpinner"
            :line-size="2"
            :speed="1"
        />

        <CheckIcon
            v-else-if="isDoneIconVisible"
            class="base-button__icon"
            width="18"
            height="18"
            :color="colorIcon"
        />

        <RemoveIcon
            v-else-if="isFailIconVisible"
            class="base-button__icon"
            width="18"
            height="18"
            :color="colorIcon"
        />

        <div v-else-if="$slots.icon" class="l-inline base-button__icon">
            <slot name="icon" />
        </div>

        <slot />
    </button>
</template>

<script>
import VSpinner from 'vue-simple-spinner'

import CheckIcon from '../icons/CheckIcon'
import RemoveIcon from '../icons/RemoveIcon'

export default {
    name: 'BaseButton',
    components: {
        CheckIcon,
        RemoveIcon,
        VSpinner,
    },
    props: {
        block: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        variant: {
            type: String,
            default: 'solid',
            validator: value =>
                ['danger', 'ghost', 'light', 'outline', 'solid'].includes(
                    value
                ),
        },
        size: {
            type: String,
            default: 'default',
        },
    },
    data() {
        return {
            isDoneIconVisible: false,
            isFailIconVisible: false,
            colorSpinner: process.env.VUE_APP_COLOR_PRIMARY,
            colorIcon: 'rgba(255, 255, 255, 0.75)',
        }
    },
    methods: {
        showDoneIcon(duration = 1000) {
            this.isDoneIconVisible = true
            setTimeout(() => {
                this.isDoneIconVisible = false
            }, duration)
        },
        showFailIcon(duration = 1000) {
            this.isFailIconVisible = true
            setTimeout(() => {
                this.isFailIconVisible = false
            }, duration)
        },
    },
}
</script>

<style lang="scss" scoped>
.base-button {
    $self: &;
    position: relative;
    padding: 20px;
    background-color: $color-primary;
    border: none;
    border-radius: 8px;
    font-weight: 700;
    font-size: 18px;
    text-align: center;
    font-family: 'IBM Plex Sans', sans-serif;
    color: $color-primary-contrast;
    transition: all 0.1s;
    outline: none;
    cursor: pointer;

    &:focus,
    &:hover {
        background-color: lighten($color-primary, 5%);
    }

    &:active {
        background-color: darken($color-primary, 1.25%);
    }

    &:disabled {
        background-color: $color-light;
        color: rgba(0, 0, 0, 0.2);
        cursor: default;
    }

    &--medium {
        padding: 10px 12px;
        font-size: 15px;

        #{$self}__icon {
            left: 12px;
        }
    }

    &--small {
        padding: 8px 16px;
        font-size: 14px;
        font-weight: 400;
        border-radius: 0;
    }

    &--danger {
        background: transparent;
        border: 1px solid $color-danger;
        color: $color-danger;

        &:hover,
        &:focus {
            background-color: $color-danger;
            color: $color-primary-contrast;
        }
    }

    &--ghost {
        background: transparent;
        border: none;
        color: $color-primary;

        &:hover {
            background-color: $color-primary-light;
            color: $color-primary-contrast;
        }
    }

    &--light {
        padding-top: 4px;
        padding-bottom: 4px;
        min-height: 30px;
        background-color: $color-pill-blue-light;
        color: $color-primary;

        &:hover,
        &:focus {
            background-color: mix($color-primary, $color-pill-blue-light, 5%);
        }
    }

    &--outline {
        border: 1px solid $color-primary;
        background-color: $color-primary-contrast;
        color: $color-primary;

        &:hover,
        &:focus {
            border-color: lighten($color-primary, 5%);
            background-color: lighten($color-primary, 5%);
            color: $color-primary-contrast;
        }
    }

    &--block {
        padding: 10px 16px;
        background-color: #fff;
        box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.08);
        border-radius: 8px;
        line-height: 20px;
        font-weight: 600;
        font-size: 15px;
        color: $color-primary-text;

        &:focus,
        &:hover {
            background-color: darken(#fff, 1%);
        }

        &:active {
            background-color: darken(#fff, 2%);
        }

        &:disabled {
            background-color: #fff;
            color: rgba($color-primary-text, 0.25);
        }
    }

    &--with-icon {
        padding-left: 40px;
    }

    &__icon {
        position: absolute;
        top: 50%;
        left: 16px;
        transform: translateY(-50%);
    }
}
</style>
